// SessionPage — Mode-aware session orchestrator
//
// Loads session metadata from Firestore, initialises useAnnaChat and
// useConceptMap, routes tool results to concept map, and renders
// SessionShell with the appropriate layout.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnnaChat } from '@hooks/useAnnaChat';
import { useConceptMap } from '@hooks/useConceptMap';
import { useVoiceInput } from '@hooks/useVoiceInput';
import { useVoiceOutput } from '@hooks/useVoiceOutput';
import { useVoiceAvailability } from '@hooks/useVoiceAvailability';
import { useSessions } from '@hooks/useSessions';
import { getTopic } from '@services/firebase/topicsService';
import { updateSession } from '@services/firebase/sessionService';
import { useAuth } from '../../contexts/AuthContext';
import SessionFramingStep from './SessionFramingStep';
import SessionShell from './SessionShell';
import { ANNA_TOOLS, EXAM_TOOLS, INTRO_TOOLS } from '@services/toolDefinitions';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { generateFindings } from '../../utils/generateFindings';
import { buildBloomStatus } from '../../utils/buildBloomStatus';
import type { TeachingForm, SessionFraming, SessionChatMessage, ConceptMapData, BloomLevel, Topic } from '../../types';

export default function SessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('session');
  const { getSessionById, endSession, fetchSessionsByTopic } = useSessions();
  const { user } = useAuth();

  // Session metadata
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teachingForm, setTeachingForm] = useState<TeachingForm>('dialog');
  const [topicTitle, setTopicTitle] = useState('');
  const [subtopicTitle, setSubtopicTitle] = useState('');
  const [topicId, setTopicId] = useState('');
  const [journeyId, setJourneyId] = useState('');
  // Stored so didacticConfig is available for useConceptMap
  const [topic, setTopic] = useState<Topic | null>(null);

  // Framing state
  const [framing, setFraming] = useState<SessionFraming | null>(null);
  const [showFraming, setShowFraming] = useState(true);

  // Track whether we've sent the initial message to Anna
  const sessionInitiatedRef = useRef(false);

  // Session history for bloom context
  const [bloomStatus, setBloomStatus] = useState('');

  // Chat context: pre-fill text for ChatPanel when user clicks a concept map node
  const [chatContext, setChatContext] = useState('');

  // Chat hook — normalise SessionFraming.pace (optional) to required string shape
  const normalisedFraming = framing
    ? { level: framing.level, pace: framing.pace ?? 'normal' }
    : undefined;

  // Form-specific tool set: each mode exposes only the tools appropriate for its pedagogy
  const tools = useMemo(() => {
    switch (teachingForm) {
      case 'introduction':
        return topic?.didacticConfig ? INTRO_TOOLS : ANNA_TOOLS;
      case 'exam':
        return EXAM_TOOLS;
      case 'dialog':
      default:
        return ANNA_TOOLS;
    }
  }, [teachingForm, topic?.didacticConfig]);

  const chat = useAnnaChat({
    teachingForm,
    topicTitle,
    subtopicTitle,
    bloomStatus: bloomStatus || undefined,
    sessionFraming: normalisedFraming,
    tools,
  });

  // Derive current Bloom level from most recent bloom update
  const currentBloomLevel = useMemo(() => {
    if (chat.bloomUpdates.length === 0) return 'recognize' as const;
    const latest = chat.bloomUpdates[chat.bloomUpdates.length - 1];
    return (latest.level || 'recognize') as 'recognize' | 'understand' | 'apply' | 'analyze' | 'evaluate' | 'create';
  }, [chat.bloomUpdates]);

  // Voice availability: determines when mic appears based on teaching form + Bloom
  const voiceAvailability = useVoiceAvailability({
    teachingForm: teachingForm as 'introduction' | 'dialog' | 'exam',
    currentBloomLevel: currentBloomLevel,
    annaHasAskedQuestion: chat.messages.some(m => m.role === 'assistant' && m.content.includes('?')),
  });

  // Voice input: STT via Deepgram
  const { i18n } = useTranslation();
  const voiceInput = useVoiceInput({
    language: i18n.language === 'da' ? 'da' : 'en',
    onFinalTranscript: (text) => {
      // Voice transcripts feed directly into the text pipeline
      chat.sendMessage(text);
    },
    autoSend: true,
  });

  const handleVoiceToggle = useCallback(() => {
    if (voiceInput.isListening) {
      voiceInput.stopListening();
    } else {
      voiceInput.startListening();
    }
  }, [voiceInput]);

  // Voice output: TTS for Anna's responses
  const [ttsEnabled, setTtsEnabled] = useState(false);

  const voiceOutput = useVoiceOutput({
    enabled: ttsEnabled,
  });

  // Interrupt Anna's audio when user starts speaking
  useEffect(() => {
    if (voiceInput.isListening && voiceOutput.isSpeaking) {
      voiceOutput.stop();
    }
  }, [voiceInput.isListening, voiceOutput.isSpeaking, voiceOutput.stop]);

  // Feed completed assistant messages to TTS
  const lastMessageCountRef = useRef(0);

  useEffect(() => {
    if (!ttsEnabled || chat.isStreaming) return;

    const currentCount = chat.messages.length;
    if (currentCount > lastMessageCountRef.current) {
      const latest = chat.messages[currentCount - 1];
      if (latest?.role === 'assistant' && latest.content.trim()) {
        voiceOutput.speak(latest.content);
      }
    }
    lastMessageCountRef.current = currentCount;
  }, [chat.messages.length, chat.isStreaming, ttsEnabled, voiceOutput]);

  // Snapshot ref for findings diff at session end
  const conceptMapSnapshotRef = useRef<ConceptMapData | null>(null)

  // Concept map hook — didacticConfig gates which actions are permitted per mode
  const conceptMap = useConceptMap({
    userId: user?.uid || '',
    topicId,
    journeyId,
    topicTitle,
    didacticConfig: topic?.didacticConfig,
  });

  // Node click in dialog (Samtale) mode: highlight the node and pre-fill chat input
  const handleNodeClick = useCallback(
    (nodeId: string, nodeLabel: string) => {
      if (teachingForm === 'dialog') {
        // Highlight the clicked node via useConceptMap
        conceptMap.processToolResult({
          id: `node-click-${nodeId}-${Date.now()}`,
          toolName: 'show_concept_map',
          input: { action: 'highlight', nodeIds: [nodeId] },
          timestamp: new Date(),
        });
        // Pre-fill chat input with node context
        setChatContext(`Om: "${nodeLabel}" — `);
      }
    },
    [teachingForm, conceptMap],
  );

  // Snapshot concept map after initial load for findings diff
  useEffect(() => {
    if (
      conceptMapSnapshotRef.current === null &&
      conceptMap.nodes.length > 0 &&
      !conceptMap.isLoading
    ) {
      const nodes = conceptMap.nodes.map(n => ({
        id: n.id,
        label: n.data?.label as string,
        bloomLevel: n.data?.bloomLevel as BloomLevel,
        pinned: (n.data?.pinned ?? false) as boolean,
        position: n.position,
        origin: (n.data?.origin ?? 'introduction') as 'introduction' | 'conversation',
      }))
      const edges = conceptMap.edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.label as string | undefined,
        relationType: (e.data?.relationType ?? 'related') as 'related' | 'interdependent' | 'causal',
      }))
      conceptMapSnapshotRef.current = { nodes, edges }
    }
  }, [conceptMap.nodes, conceptMap.edges, conceptMap.isLoading])

  // Update bloom status with concept map node count once loaded
  const bloomStatusUpdatedRef = useRef(false);
  useEffect(() => {
    if (
      bloomStatus &&
      !bloomStatusUpdatedRef.current &&
      conceptMap.nodes.length > 0 &&
      !conceptMap.isLoading
    ) {
      bloomStatusUpdatedRef.current = true;
      const nodeCountLine = `Concept map has ${conceptMap.nodes.length} nodes.`;
      setBloomStatus(prev => {
        if (prev.includes('Concept map has ')) {
          return prev.replace(/Concept map has \d+ nodes\./, nodeCountLine);
        }
        const lines = prev.split('\n');
        const returningIdx = lines.findIndex(l => l.startsWith('This is a returning'));
        if (returningIdx >= 0) {
          lines.splice(returningIdx, 0, nodeCountLine);
          return lines.join('\n');
        }
        return prev;
      });
    }
  }, [bloomStatus, conceptMap.nodes.length, conceptMap.isLoading]);

  // Update concept map meta when session starts (once per mount, after loading)
  const metaUpdatedRef = useRef(false);
  useEffect(() => {
    if (!loading && !metaUpdatedRef.current && teachingForm) {
      metaUpdatedRef.current = true;
      conceptMap.updateMeta({
        lastModifiedIn: teachingForm === 'introduction' ? 'introduction' : 'conversation',
        totalSessions: (conceptMap.meta?.totalSessions ?? 0) + 1,
      });
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route show_concept_map tool results to useConceptMap (deduplicated)
  // Inject _origin so useConceptMap can distinguish introduction vs conversation nodes
  const processedIdsRef = useRef(new Set<string>());
  useEffect(() => {
    chat.toolResults.forEach((tr) => {
      if (tr.toolName === 'show_concept_map' && !processedIdsRef.current.has(tr.id)) {
        processedIdsRef.current.add(tr.id);
        const enrichedTr = {
          ...tr,
          input: {
            ...(tr.input as Record<string, unknown>),
            _origin: teachingForm === 'introduction' ? 'introduction' : 'conversation',
          },
        };
        conceptMap.processToolResult(enrichedTr);
      }
    });
  }, [chat.toolResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load session metadata on mount
  useEffect(() => {
    if (!sessionId) {
      setError(t('page.notFound'));
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const session = await getSessionById(sessionId!);
        if (cancelled) return;

        if (!session) {
          setError(t('page.notFound'));
          setLoading(false);
          return;
        }

        // If session is already completed, redirect to summary
        if (session.status === 'completed') {
          navigate(`/session/summary/${sessionId}`, { replace: true });
          return;
        }

        setTeachingForm(session.teachingForm);
        setTopicTitle(session.topicTitle || '');
        setJourneyId(session.learningJourneyId || '');

        // Fetch previous completed sessions for bloom history
        if (session.topicId) {
          const allSessions = await fetchSessionsByTopic(session.topicId);
          const completedPrevious = allSessions
            .filter(s => s.status === 'completed' && s.id !== sessionId)
            .slice(0, 3);

          if (completedPrevious.length > 0 && !cancelled) {
            setBloomStatus(buildBloomStatus(completedPrevious, 0, session.teachingForm));
          }
        }

        // Resolve topic document (with didacticConfig) BEFORE setting topicId.
        // This ensures useConceptMap receives both topicId and didacticConfig
        // in the same React render batch, avoiding a race where it initializes
        // without didacticConfig and then Anna uses add_nodes instead of activate_node.
        const jId = session.learningJourneyId;
        const subtopicId = session.subtopicIds?.[0];

        if (jId && session.topicId) {
          const topicResult = await getTopic(jId, session.topicId);
          if (!cancelled && topicResult.success && topicResult.topic) {
            setTopic(topicResult.topic);
            const sub = topicResult.topic.subtopics.find(
              (s) => s.id === subtopicId,
            );
            setSubtopicTitle(sub?.title || session.topicTitle || '');
          }
        }

        // Set topicId AFTER topic is loaded so useConceptMap gets didacticConfig
        // in the same render batch (avoids premature load without config)
        setTopicId(session.topicId || '');

        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load session');
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFramingSubmit = useCallback((f: SessionFraming) => {
    setFraming(f);
    setShowFraming(false);
  }, []);

  const handleFramingSkip = useCallback(() => {
    setShowFraming(false);
  }, []);

  // Auto-send initial message to Anna once framing is done and session is loaded
  useEffect(() => {
    if (
      !showFraming &&
      !loading &&
      !sessionInitiatedRef.current &&
      topicTitle
    ) {
      sessionInitiatedRef.current = true;
      const topic = subtopicTitle || topicTitle;
      // Contextual starter based on teaching form — gives Anna the cue to begin
      const starters: Record<string, string> = {
        introduction: t('chat.starterIntroduction', { topic }),
        dialog: t('chat.starterDialog', { topic }),
        exam: t('chat.starterExam', { topic }),
      };
      const starter = starters[teachingForm] || starters.introduction;
      chat.sendMessage(starter);
    }
  }, [showFraming, loading, topicTitle, subtopicTitle, teachingForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnd = useCallback(async () => {
    if (!sessionId) return;

    // Serialize chat messages for persistence
    const chatHistory: SessionChatMessage[] = chat.messages.map(msg => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp.toISOString(),
      toolCalls: msg.toolCalls?.map(tc => ({ toolName: tc.name })),
    }));

    // Save chat history to session document
    if (chatHistory.length > 0) {
      await updateSession(sessionId, { chatHistory });
    }

    // Persist bloom data from this session
    if (chat.bloomUpdates.length > 0) {
      const bloomDist: Record<string, number> = {};
      for (const update of chat.bloomUpdates) {
        bloomDist[update.level] = (bloomDist[update.level] || 0) + 1;
      }

      const BLOOM_ORDER = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];
      let highestLevel = '';
      for (const level of BLOOM_ORDER) {
        if (bloomDist[level]) highestLevel = level;
      }

      if (highestLevel) {
        await updateSession(sessionId, {
          bloomLevelReached: highestLevel,
          bloomDistribution: bloomDist,
        });
      }
    }

    // Generate findings from concept map changes
    if (conceptMapSnapshotRef.current && user && topicId) {
      const currentNodes = conceptMap.nodes.map(n => ({
        id: n.id,
        label: n.data?.label as string,
        bloomLevel: n.data?.bloomLevel as BloomLevel,
        pinned: (n.data?.pinned ?? false) as boolean,
        position: n.position,
        origin: (n.data?.origin ?? 'introduction') as 'introduction' | 'conversation',
      }))
      const currentEdges = conceptMap.edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.label as string | undefined,
        relationType: (e.data?.relationType ?? 'related') as 'related' | 'interdependent' | 'causal',
      }))
      const after: ConceptMapData = { nodes: currentNodes, edges: currentEdges }
      const newFindings = generateFindings(
        conceptMapSnapshotRef.current,
        after,
        sessionId,
        teachingForm,
      )
      if (newFindings.length > 0) {
        const noteRef = doc(db, 'topicNotes', `${user.uid}_${topicId}`)
        const existingDoc = await getDoc(noteRef)
        const existingFindings = existingDoc.data()?.findings ?? []
        await setDoc(noteRef, {
          findings: [...existingFindings, ...newFindings],
        }, { merge: true })
      }
    }

    await endSession(sessionId);
    navigate(`/session/summary/${sessionId}?justCompleted=true`);
  }, [sessionId, endSession, navigate, chat.messages, chat.bloomUpdates, user, topicId, teachingForm, conceptMap.nodes, conceptMap.edges]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-dvh bg-gray-950">
        <div className="text-gray-400 text-sm">
          {t('connection.connecting')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-dvh bg-gray-950 gap-4">
        <p className="text-red-400 text-sm">{error}</p>
        <button
          onClick={() => navigate('/sessions')}
          className="text-sm text-gray-400 hover:text-gray-200 underline"
        >
          {t('summary.backToTeaching')}
        </button>
      </div>
    );
  }

  if (showFraming) {
    return (
      <div className="h-dvh bg-gray-950 flex items-center justify-center">
        <SessionFramingStep
          onSubmit={handleFramingSubmit}
          onSkip={handleFramingSkip}
          teachingForm={teachingForm}
        />
      </div>
    );
  }

  return (
    <SessionShell
      teachingForm={teachingForm}
      subtopicTitle={subtopicTitle}
      messages={chat.messages}
      toolResults={chat.toolResults}
      bloomUpdates={chat.bloomUpdates}
      isStreaming={chat.isStreaming}
      error={chat.error}
      sendMessage={chat.sendMessage}
      clearError={chat.clearError}
      onEnd={handleEnd}
      conceptMapNodes={conceptMap.nodes}
      conceptMapEdges={conceptMap.edges}
      conceptMapLoading={conceptMap.isLoading}
      onNodeDragStop={conceptMap.onNodeDragStop}
      currentBloomLevel={currentBloomLevel}
      totalSessions={conceptMap.meta?.totalSessions ?? 0}
      voiceAvailability={voiceAvailability}
      voiceState={voiceInput.state}
      voiceError={voiceInput.error}
      audioLevel={voiceInput.audioLevel}
      interimTranscript={voiceInput.interimTranscript}
      onVoiceToggle={handleVoiceToggle}
      voiceOutputEnabled={ttsEnabled}
      isAnnaSpeaking={voiceOutput.isSpeaking}
      onVoiceOutputToggle={() => setTtsEnabled(prev => !prev)}
      onNodeClick={handleNodeClick}
      prefillText={chatContext}
      onPrefillConsumed={() => setChatContext('')}
    />
  );
}
